import { useEffect, useState } from "react";
import type { Column } from "../components/DataTable";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import { useBusy } from "../../context/BusyContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import "../styles/adminMarketAnalysisPlans.css";

type ModalMode = "add" | "edit" | "view";

const normalizeBulletPoints = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
    } catch {
      return [];
    }
  }

  return [];
};

export default function AdminMarketAnalysisPlans() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();
  const { runBusy } = useBusy();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);
  const [bulletPoints, setBulletPoints] = useState<string[]>([""]);

  const isViewMode = modalMode === "view";

  const fetchPlans = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE}/admin/market-analysis-plans`, {
          credentials: "include",
        }),
      );

      const result = await res.json();

      const freshData = Array.isArray(result.data)
        ? result.data
        : result.data
          ? [result.data]
          : [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to fetch Market Analysis plans.",
        });
      }
    } catch (error) {
      console.error("Fetch Market Analysis Plans Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to fetch Market Analysis plans.",
      });
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openModal = (mode: ModalMode, row?: any) => {
    setModalMode(mode);

    const selected = row || {
      title: "",
      amount: "",
      description: "",
      is_active: 1,
    };

    setActiveRow(selected);

    const existingBullets = normalizeBulletPoints(selected.bullet_points);
    setBulletPoints(existingBullets.length ? existingBullets : [""]);

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveRow(null);
    setBulletPoints([""]);
  };

  const updateActiveRow = (key: string, value: any) => {
    setActiveRow((prev: any) => ({
      ...(prev || {}),
      [key]: value,
    }));
  };

  const updateBulletPoint = (index: number, value: string) => {
    setBulletPoints((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? value : item)),
    );
  };

  const addBulletPoint = () => {
    setBulletPoints((prev) => [...prev, ""]);
  };

  const removeBulletPoint = (index: number) => {
    setBulletPoints((prev) =>
      prev.length === 1
        ? [""]
        : prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const handleSubmit = async () => {
    try {
      const title = String(activeRow?.title || "").trim();
      const amount = Number(activeRow?.amount || 0);
      const description = String(activeRow?.description || "").trim();
      const searchChargeAmount = Number(activeRow?.search_charge_amount || 1);
      const isActive = Number(activeRow?.is_active) === 1 ? 1 : 0;

      console.log(title, amount, description, searchChargeAmount, isActive);

      const cleanedBulletPoints = bulletPoints
        .map((item) => item.trim())
        .filter(Boolean);

      if (!title) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "Plan title is required.",
        });
        return;
      }

      if (!amount || amount <= 0) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "Plan amount must be greater than 0.",
        });
        return;
      }

      if (!searchChargeAmount || searchChargeAmount <= 0) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "Search charge amount must be greater than 0.",
        });
        return;
      }

      if (!cleanedBulletPoints.length) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "Please add at least one bullet point.",
        });
        return;
      }

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/market-analysis-plans`
          : `${BASE}/admin/market-analysis-plans/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title,
            amount,
            search_charge_amount: searchChargeAmount,
            description,
            bullet_points: cleanedBulletPoints,
            is_active: isActive,
          }),
        }),
      );

      //   console.log(res, "Plans payload");

      const result = await res.json();

      if (result.success) {
        await fetchPlans();
        closeModal();

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Market Analysis plan added successfully."
              : "Market Analysis plan updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save Market Analysis plan.",
        });
      }
    } catch (error) {
      console.error("Submit Market Analysis Plan Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to save Market Analysis plan.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Deactivate Plan",
      message:
        "Are you sure you want to deactivate this Market Analysis plan? It will no longer appear on the website pricing page.",
      confirmText: "Deactivate",
      onConfirm: async () => {
        try {
          const res = await runBusy(() =>
            fetch(`${BASE}/admin/market-analysis-plans/${row.id}`, {
              method: "DELETE",
              credentials: "include",
            }),
          );

          const result = await res.json();

          if (result.success) {
            await fetchPlans();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deactivated",
              message: "Market Analysis plan deactivated successfully.",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message:
                result.message || "Failed to deactivate Market Analysis plan.",
            });
          }
        } catch (error) {
          console.error("Delete Market Analysis Plan Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Failed to deactivate Market Analysis plan.",
          });
        }
      },
    });
  };

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Plan Title" },
    {
      key: "amount",
      label: "Amount",
      render: (row: any) =>
        `RM ${Number(row.amount || 0).toLocaleString("en-MY", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: "search_charge_amount",
      label: "Search Charge",
      render: (row: any) =>
        `RM ${Number(row.search_charge_amount || 0).toLocaleString("en-MY", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: "description",
      label: "Description",
      render: (row: any) => row.description || "-",
    },
    {
      key: "bullet_points",
      label: "Bullets",
      render: (row: any) => {
        const bullets = normalizeBulletPoints(row.bullet_points);
        return `${bullets.length} item(s)`;
      },
    },
    {
      key: "is_active",
      label: "Status",
      render: (row: any) => {
        const isActive = Number(row.is_active) === 1;

        return (
          <span className={isActive ? "badge-success" : "badge-danger"}>
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: any) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Market Analysis Plans"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => openModal("add")}
        onView={(row) => openModal("view", row)}
        onEdit={(row) => openModal("edit", row)}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      {modalOpen && (
        <div className="market-plan-modal-overlay">
          <div className="market-plan-modal">
            <div className="market-plan-modal-header">
              <h3>
                {modalMode === "add"
                  ? "Add Market Analysis Plan"
                  : modalMode === "edit"
                    ? "Edit Market Analysis Plan"
                    : "View Market Analysis Plan"}
              </h3>

              <button type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="market-plan-modal-body">
              {modalMode !== "add" && (
                <input
                  className="market-plan-input disabled"
                  value="Auto Generated"
                  disabled
                />
              )}

              <div className="market-plan-grid">
                <div className="market-plan-field">
                  <label>Plan Title</label>
                  <input
                    value={activeRow?.title || ""}
                    disabled={isViewMode}
                    onChange={(e) => updateActiveRow("title", e.target.value)}
                  />
                </div>

                <div className="market-plan-field">
                  <label>Amount</label>
                  <input
                    type="number"
                    value={activeRow?.amount || ""}
                    disabled={isViewMode}
                    onChange={(e) => updateActiveRow("amount", e.target.value)}
                  />
                </div>

                <div className="market-plan-field">
                  <label>Description</label>
                  <textarea
                    value={activeRow?.description || ""}
                    disabled={isViewMode}
                    onChange={(e) =>
                      updateActiveRow("description", e.target.value)
                    }
                  />
                </div>

                <div className="market-plan-field">
                  <label>Search Charge Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={activeRow?.search_charge_amount || ""}
                    disabled={isViewMode}
                    onChange={(e) =>
                      updateActiveRow("search_charge_amount", e.target.value)
                    }
                  />
                </div>

                <div className="market-plan-field">
                  <label>Status</label>
                  <select
                    value={Number(activeRow?.is_active) === 1 ? 1 : 0}
                    disabled={isViewMode}
                    onChange={(e) =>
                      updateActiveRow("is_active", Number(e.target.value))
                    }
                  >
                    <option value="">Select</option>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="market-plan-bullets-section">
                <div className="market-plan-bullets-header">
                  <label>Bullet Points</label>

                  {!isViewMode && (
                    <button type="button" onClick={addBulletPoint}>
                      + Add
                    </button>
                  )}
                </div>

                <div className="market-plan-bullets-list">
                  {bulletPoints.map((point, index) => (
                    <div className="market-plan-bullet-row" key={index}>
                      <input
                        value={point}
                        disabled={isViewMode}
                        placeholder={`Bullet point ${index + 1}`}
                        onChange={(e) =>
                          updateBulletPoint(index, e.target.value)
                        }
                      />

                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => removeBulletPoint(index)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="market-plan-modal-footer">
              <button
                type="button"
                className="market-plan-cancel-btn"
                onClick={closeModal}
              >
                {isViewMode ? "Close" : "Cancel"}
              </button>

              {!isViewMode && (
                <button
                  type="button"
                  className="market-plan-submit-btn"
                  onClick={handleSubmit}
                >
                  {modalMode === "add" ? "Create" : "Update"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
